import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

import type { Chat, SendMessageInput } from '@/shared/api';

const draftsByChatId = new Map<Chat['id'], string>();

const useMessageCreateContentState = (activeChatId: MaybeRefOrGetter<Chat['id'] | null> = null) => {
  const messageText = ref<SendMessageInput['content']>('');
  let currentChatId: Chat['id'] | null = null;

  watch(
    () => toValue(activeChatId),
    (chatId): void => {
      currentChatId = chatId;
      messageText.value = chatId ? (draftsByChatId.get(chatId) ?? '') : '';
    },
    { flush: 'sync', immediate: true },
  );

  watch(
    messageText,
    (draft): void => {
      if (currentChatId) {
        draftsByChatId.set(currentChatId, draft);
      }
    },
    { flush: 'sync' },
  );

  const messageContent = computed<SendMessageInput>(() => {
    const mes: SendMessageInput = {
      content: '',
      type: 'text',
    };

    if (isMessageContentValid.value) {
      mes.content = messageText.value.trim();
    }

    return mes;
  });

  const isMessageTextExists = computed<boolean>(() => messageText.value.trim().length !== 0);
  const isMessageContentValid = computed<boolean>(() => isMessageTextExists.value);

  const resetMessageContent = (): void => {
    messageText.value = '';
    if (currentChatId) {
      draftsByChatId.delete(currentChatId);
    }
  };

  return { messageText, messageContent, isMessageContentValid, resetMessageContent };
};

export { useMessageCreateContentState };
